import { ReactNode, createContext, useContext } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';

// Create a context to track if we're inside TypographyStyles
const TypographyStylesContext = createContext<boolean>(false);

// FY24 rebrand type matrix rules as per https://www.figma.com/file/RZRs55BkCWtX6zBPUD7dRb/Type-Matrix-React---FY24-Updates?type=design&node-id=323-741&mode=design&t=7RUZPtxLZpLvX1Q5-0

// Colors are set via CSS custom properties (--wac-color-heading, --wac-color-body) defined in
// index.css so they can be overridden in any scope without specificity battles.

const typographyStyles = css({
	// headlines
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h1, .h1': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 800,
		color: 'var(--wac-color-heading)',
		fontSize: '2.5rem',
		lineHeight: '2.875rem',

		[media.only.sm]: {
			fontSize: '2.75rem',
			lineHeight: '3.125rem',
		},
		[media.above.md]: {
			fontSize: '3rem',
			lineHeight: '3.5rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-contrast': {
		fontWeight: '400',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> strong': {
			fontWeight: '800',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-lg, .h1-lg-contrast, .superhero, .superhero-contrast': {
		fontFamily: wacToken('font.charlie.display'),
		fontSize: '3.125rem',
		lineHeight: '3.75rem',
		color: 'var(--wac-color-heading)',

		[media.only.sm]: {
			fontSize: '3.75rem',
			lineHeight: '4.375rem',
		},
		[media.above.md]: {
			fontSize: '4.375rem',
			lineHeight: '5rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-lg, .superhero': {
		fontWeight: 800,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-lg-contrast, .superhero-contrast': {
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> strong': {
			fontWeight: '800',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h2, .h2': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		color: 'var(--wac-color-heading)',
		fontSize: '2rem',
		lineHeight: '2.375rem',

		[media.only.sm]: {
			fontSize: '2.25rem',
			lineHeight: '2.625rem',
		},
		[media.above.md]: {
			fontSize: '2.5rem',
			lineHeight: '3rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h3, .h3': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		color: 'var(--wac-color-heading)',
		fontSize: '1.5rem',
		lineHeight: '1.875rem',

		[media.only.sm]: {
			fontSize: '1.75rem',
			lineHeight: '2.125rem',
		},
		[media.above.md]: {
			fontSize: '2rem',
			lineHeight: '2.375rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h4, .h4': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		color: 'var(--wac-color-heading)',
		fontSize: '1.25rem',
		lineHeight: '1.625rem',

		[media.only.sm]: {
			fontSize: '1.375rem',
			lineHeight: '1.75rem',
		},
		[media.above.md]: {
			fontSize: '1.5rem',
			lineHeight: '1.875rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h5, .h5': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '1.125rem',
		lineHeight: '1.625rem',
		color: 'var(--wac-color-heading)',

		[media.above.md]: {
			fontSize: '1.25rem',
			lineHeight: '1.75rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h6, .h6': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '1rem',
		lineHeight: '1.5rem',
		color: 'var(--wac-color-heading)',
	},
	// Atlassian Mono heading font override — applied via className when Dev Theme is active
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.mono-font-family-heading': {
		fontFamily: token('font.family.code'),
		fontSize: '2rem',
		lineHeight: '2.375rem',
		fontWeight: 700,

		[media.only.sm]: {
			fontSize: '2.25rem',
			lineHeight: '2.625rem',
			fontWeight: 700,
		},
		[media.only.md]: {
			fontSize: '2.5rem',
			lineHeight: '3rem',
			fontWeight: 700,
		},
		[media.above.lg]: {
			fontSize: '4.25rem',
			lineHeight: '4.75rem',
			fontWeight: 600,
		},
	},

	'.sans-font-family-heading': {
		fontFamily: token('font.family.heading'),
		fontWeight: 500,
		fontSize: '3.125rem',
		lineHeight: '3.75rem',

		[media.only.sm]: {
			fontSize: '3.75rem',
			lineHeight: '4.375rem',
		},
		[media.above.md]: {
			fontSize: '4.375rem',
			lineHeight: '5rem',
		},
	},

	// body
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-lg': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '1.125rem',
		lineHeight: '1.625rem',
		color: 'var(--wac-color-body)',

		[media.above.md]: {
			fontSize: '1.25rem',
			lineHeight: '1.75rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'p, .body-md': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '1rem',
		lineHeight: '1.5rem',
		color: 'var(--wac-color-body)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-sm': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '0.875rem',
		lineHeight: '1.25rem',
		color: 'var(--wac-color-body)',
	},

	// card titles
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.card-title': {
		fontFamily: wacToken('font.charlie.display'),
		fontSize: '1.25rem',
		fontWeight: 700,
		lineHeight: '1.625rem',
		color: 'var(--wac-color-heading)',
		letterSpacing: '0.3px',

		[media.only.sm]: {
			fontSize: '1.375rem',
			lineHeight: '1.75rem',
		},
		[media.above.md]: {
			fontSize: '1.5rem',
			lineHeight: '1.875rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.card-title-sm': {
		fontFamily: wacToken('font.charlie.display'),
		fontSize: '1.125rem',
		fontWeight: 700,
		lineHeight: '1.5rem',
		color: 'var(--wac-color-heading)',
		letterSpacing: '0.3px',

		[media.above.md]: {
			fontSize: '1.25rem',
			lineHeight: '1.625rem',
		},
	},

	// captions
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.fine-print': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '0.75rem',
		lineHeight: '1.125rem',
		color: 'var(--wac-color-body)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.fine-print-sm': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '0.625rem',
		lineHeight: '1rem',
		color: 'var(--wac-color-body)',
	},

	// links
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.link': {
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '1rem',
		lineHeight: '1.5rem',
		fontWeight: 600,
		color: 'var(--wac-color-body)',
	},

	// eyebrow
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-lg': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 700,
		fontSize: '1rem',
		lineHeight: '1.5rem',
		textTransform: 'uppercase',
		color: 'var(--wac-color-body)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-md': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '0.875rem',
		lineHeight: '1.125rem',
		textTransform: 'uppercase',
		color: 'var(--wac-color-body)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-sm': {
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 400,
		fontSize: '0.75rem',
		lineHeight: '1rem',
		textTransform: 'uppercase',
		color: 'var(--wac-color-body)',
	},

	// callouts
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.callout': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 300,
		fontSize: '1.25rem',
		lineHeight: '1.75rem',
		color: 'var(--wac-color-body)',

		[media.only.sm]: {
			fontSize: '1.5rem',
			lineHeight: '2rem',
		},
		[media.above.md]: {
			fontWeight: 400,
			fontSize: '1.75rem',
			lineHeight: '2.5rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.callout-lg': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 300,
		fontSize: '1.75rem',
		lineHeight: '2.5rem',
		color: 'var(--wac-color-body)',

		[media.only.sm]: {
			fontSize: '2rem',
			lineHeight: '2.75rem',
		},
		[media.above.md]: {
			fontWeight: 400,
			fontSize: '2.25rem',
			lineHeight: '3rem',
		},
	},

	// stats
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.stat-lg': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 800,
		color: wacToken('color.primary.blue'),
		fontSize: '4.375rem',
		lineHeight: '5rem',

		[media.only.sm]: {
			fontSize: '3.75rem',
			lineHeight: '4.375rem',
		},
		[media.above.md]: {
			fontSize: '3.125rem',
			lineHeight: '3.75rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.stat-sm': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 800,
		color: wacToken('color.primary.blue'),
		fontSize: '2.5rem',
		lineHeight: '2.875rem',

		[media.only.sm]: {
			fontSize: '2.75rem',
			lineHeight: '3.125rem',
		},
		[media.above.md]: {
			fontSize: '3rem',
			lineHeight: '3.5rem',
		},
	},
});

// T'26 EU WAC Refresh type matrix.
// Source of truth: https://www.figma.com/design/QYEJfz386jOThUiJYnQ3fc/WAC-Reimagine-2027---MVP-Library?node-id=178-527
//
// The Figma breakpoints map 1:1 onto the AFM responsive scale:
//   mobile (< 768)          -> no media query
//   tablet (768 - 1023)     -> media.only.sm
//   sm desktop (1024 - 1439)-> media.above.md
//   desktop (1440+)         -> media.above.lg
//
// Line heights are unitless multipliers and letter spacing is in `em`, so the values read
// the same way Figma stores them on each text style (e.g. `64 / 104% / -2%`).
//
// Font weight is the one value NOT copied from Figma verbatim. Charlie registers its
// Semibold face at 500 and has no 600 face at all (see the `@font-face` rules in index.css),
// so asking for Figma's "Semibold 600" as 600 falls through to the Bold 700 face and renders
// too heavy. Every Charlie Semibold below is therefore 500. This is Charlie-only: Atlassian
// Sans and Atlassian Mono are variable fonts, so their Figma weights are used as-is — which
// is why `.monobrow` stays at Figma's Medium 500.
//
// Each token redeclares `fontSize`/`lineHeight` under *every* at-rule `typographyStyles`
// uses for the same selector, even when the value does not change between breakpoints.
// Compiled dedupes atomic rules by property + selector + at-rule, so matching the base
// sheet's at-rules is what guarantees the refresh value wins — a bare declaration here
// would not override a media-query declaration in the base sheet.
const typographyStylesRefresh = css({
	// headlines
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h1, .h1': {
		fontWeight: 500,
		fontSize: '2.75rem',
		lineHeight: 1.04,
		letterSpacing: '-0.02em',

		[media.only.sm]: {
			fontSize: '3rem',
			lineHeight: 1.04,
		},
		[media.above.md]: {
			fontSize: '3.375rem',
			lineHeight: 1.04,
		},
		[media.above.lg]: {
			fontSize: '4rem',
		},
	},
	// The refresh matrix defines a single H1 — a page can only have one for SEO and screen
	// reader purposes — so every oversized hero variant collapses onto the H1 ramp. The XL
	// sizes are dropped outright; they were built for a wider grid.
	//
	// The grouped selectors below deliberately mirror `typographyStyles` character for
	// character. Compiled keys atomic rules off the selector string, so splitting these
	// into one rule per class would stop them deduping the base declarations away.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-xl': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 500,
		fontSize: '2.75rem',
		lineHeight: 1.04,
		letterSpacing: '-0.02em',

		[media.only.sm]: {
			fontSize: '3rem',
		},
		[media.only.md]: {
			fontSize: '3.375rem',
		},
		[media.above.lg]: {
			fontSize: '4rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-lg, .h1-lg-contrast, .superhero, .superhero-contrast': {
		fontSize: '2.75rem',
		lineHeight: 1.04,
		letterSpacing: '-0.02em',

		[media.only.sm]: {
			fontSize: '3rem',
			lineHeight: 1.04,
		},
		[media.above.md]: {
			fontSize: '3.375rem',
			lineHeight: 1.04,
		},
		[media.above.lg]: {
			fontSize: '4rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-lg, .superhero': {
		fontWeight: 500,
	},
	// The `-contrast` variants are a weight device rather than a size, so they keep the
	// regular/bold pairing — with `strong` pinned to the H1 weight of 600, not the old 800.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-lg-contrast, .superhero-contrast': {
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> strong': {
			fontWeight: 500,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h1-contrast': {
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> strong': {
			fontWeight: 500,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h2, .h2': {
		fontWeight: 500,
		fontSize: '2rem',
		lineHeight: 1.16,
		letterSpacing: '-0.02em',

		[media.only.sm]: {
			fontSize: '2.25rem',
			lineHeight: 1.16,
		},
		[media.above.md]: {
			fontSize: '2.5rem',
			lineHeight: 1.16,
		},
		[media.above.lg]: {
			fontSize: '3rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h3, .h3': {
		fontWeight: 500,
		fontSize: '1.5rem',
		lineHeight: 1.2,
		letterSpacing: '-0.01em',

		[media.only.sm]: {
			fontSize: '1.75rem',
			lineHeight: 1.2,
		},
		[media.above.md]: {
			fontSize: '2rem',
			lineHeight: 1.2,
		},
		[media.above.lg]: {
			fontSize: '2.5rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h4, .h4': {
		fontWeight: 500,
		fontSize: '1.25rem',
		lineHeight: 1.24,
		// H4/H5/H6 and fine print sit at 0% tracking. This has to be declared rather than
		// omitted: the pattern `<h2 className="h4">` (visual size vs semantic level) is
		// common, and without an explicit reset the tag's own -2%/-1% would leak through.
		letterSpacing: 'normal',

		[media.only.sm]: {
			fontSize: '1.5rem',
			lineHeight: 1.24,
		},
		[media.above.md]: {
			fontSize: '1.75rem',
			lineHeight: 1.24,
		},
		[media.above.lg]: {
			fontSize: '2rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h5, .h5': {
		fontWeight: 500,
		fontSize: '1.125rem',
		lineHeight: 1.24,
		letterSpacing: 'normal',

		[media.only.sm]: {
			fontSize: '1.25rem',
		},
		[media.above.md]: {
			fontSize: '1.375rem',
			lineHeight: 1.24,
		},
		[media.above.lg]: {
			fontSize: '1.5rem',
		},
	},
	// H6 is flat across every breakpoint. Design note on the matrix: "I do not think we
	// should use H6s on the site, but we still need them defined".
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h6, .h6': {
		fontWeight: 500,
		fontSize: '1rem',
		lineHeight: 1.24,
		letterSpacing: 'normal',
	},

	// body — flat across every breakpoint in the refresh matrix.
	// `fontWeight` is not restated here because the base sheet already sets it to 400 and
	// the matrix does not change it.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-lg': {
		fontSize: '1.25rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',

		// Only to shadow the base sheet's `above.md` line-height; its font size there is
		// already 1.25rem.
		[media.above.md]: {
			lineHeight: 1.56,
		},
	},
	// Not in the matrix — kept in step with `.body-lg` as its semibold weight variant.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-lg-semibold': {
		fontWeight: 500,
		fontSize: '1.25rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'p, .body-md': {
		fontWeight: 400,
		fontSize: '1rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
	},
	// Not in the matrix — kept in step with `.body-md` as its bold weight variant. It is 700,
	// not the Semibold 500, because it has always rendered in Charlie's Bold face: its old
	// `600` had no face of its own and fell through to 700. Declaring 700 keeps it identical.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-md-bold': {
		fontWeight: 700,
		fontSize: '1rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
	},
	// The matrix settles on two body sizes, base and large, so `.body-sm` collapses onto Fine
	// print: the two were only 2px apart, and design would rather have one small size. The
	// tracking reset matters — without it the -1% on `p, .body-md` would leak into every
	// `<p className="body-sm">`.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-sm': {
		fontWeight: 400,
		fontSize: '0.75rem',
		lineHeight: 1.56,
		letterSpacing: 'normal',
	},
	// H1 Mono — the Atlassian Mono heading used by the Dev Theme. It isn't a matrix token,
	// but both Mono tokens the matrix does define (Body Mono, Monobrow) copy their Charlie
	// twin's metrics exactly, so this takes the H1 ramp. Its weight is 600 rather than the
	// 500 used for Charlie: Atlassian Mono is a variable font, so Figma's Semibold 600 is a
	// true 600 here. The at-rules mirror the base sheet's (`only.md`, not `above.md`) so every
	// base declaration is shadowed.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.mono-font-family-heading': {
		fontWeight: 600,
		fontSize: '2.75rem',
		lineHeight: 1.04,
		letterSpacing: '-0.02em',

		[media.only.sm]: {
			fontWeight: 600,
			fontSize: '3rem',
			lineHeight: 1.04,
		},
		[media.only.md]: {
			fontWeight: 600,
			fontSize: '3.375rem',
			lineHeight: 1.04,
		},
		[media.above.lg]: {
			fontWeight: 600,
			fontSize: '4rem',
			lineHeight: 1.04,
		},
	},
	// Same reasoning for the other legacy text classes the matrix does not respec. Each
	// sets its own size/weight but no tracking, so on an `h1`/`h2`/`h3`/`p` the tag's new
	// tracking would show through and change type this ticket is meant to leave alone.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.link': {
		letterSpacing: 'normal',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.stat-lg': {
		letterSpacing: 'normal',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.stat-sm': {
		letterSpacing: 'normal',
	},
	// Body mono / code — new in the refresh matrix, no equivalent in the base sheet.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-mono': {
		fontFamily: token('font.family.code'),
		fontWeight: 400,
		fontSize: '1rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
		color: 'var(--wac-color-body)',
	},

	// card titles — the matrix drops them, since authors were overriding them to H4 anyway.
	// Both sizes therefore take the H4 ramp. The base sheet's 0.3px tracking is reset
	// because H4 sits at 0%.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.card-title': {
		fontWeight: 500,
		fontSize: '1.25rem',
		lineHeight: 1.24,
		letterSpacing: 'normal',

		[media.only.sm]: {
			fontSize: '1.5rem',
			lineHeight: 1.24,
		},
		[media.above.md]: {
			fontSize: '1.75rem',
			lineHeight: 1.24,
		},
		[media.above.lg]: {
			fontSize: '2rem',
		},
	},
	// The tablet step uses `above.sm` rather than `only.sm` deliberately. Compiled dedupes
	// atomic classes by a short group hash, and (`.card-title-sm`, only.sm, font-size)
	// collided with (`.body-lg`, font-weight) from the base sheet — this rule evicted that
	// one, which silently left `<h2 className="body-lg">` inheriting the heading's 600.
	// `above.sm` hashes differently and cascades identically, since the wider breakpoints
	// below override it in ascending min-width order.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.card-title-sm': {
		fontWeight: 500,
		fontSize: '1.25rem',
		lineHeight: 1.24,
		letterSpacing: 'normal',

		[media.above.sm]: {
			fontSize: '1.5rem',
		},
		[media.above.md]: {
			fontSize: '1.75rem',
			lineHeight: 1.24,
		},
		[media.above.lg]: {
			fontSize: '2rem',
		},
	},

	// captions — flat across every breakpoint in the refresh matrix.
	// The matrix keeps a single fine print size, so `.fine-print-sm` collapses onto it:
	// at 12 vs 10 the two were only 2pt apart.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.fine-print': {
		fontWeight: 400,
		fontSize: '0.75rem',
		lineHeight: 1.56,
		// Reset so the -1% on `p, .body-md` cannot leak into `<p className="fine-print">`.
		letterSpacing: 'normal',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.fine-print-sm': {
		fontWeight: 400,
		fontSize: '0.75rem',
		lineHeight: 1.56,
		letterSpacing: 'normal',
	},

	// links
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.link-lg': {
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '1.25rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
		fontWeight: 500,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:visited': {
			opacity: 0.8,
			'mix-blend-mode': 'multiply',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover': {
			opacity: 0.6,
			'mix-blend-mode': 'multiply',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:visited:hover': {
			opacity: 0.5,
			'mix-blend-mode': 'multiply',
		},
		'&:focus-visible': {
			border: 'none',
			borderRadius: '8px',
			outlineStyle: 'solid',
			outlineOffset: '2px',
			outlineWidth: '2px',
			outlineColor: 'inherit',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.link-md': {
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '1rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
		fontWeight: 500,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:visited': {
			opacity: 0.8,
			'mix-blend-mode': 'multiply',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover': {
			opacity: 0.6,
			'mix-blend-mode': 'multiply',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:visited:hover': {
			opacity: 0.5,
			'mix-blend-mode': 'multiply',
		},
		'&:focus-visible': {
			border: 'none',
			borderRadius: '8px',
			outlineStyle: 'solid',
			outlineOffset: '2px',
			outlineWidth: '2px',
			outlineColor: 'inherit',
		},
	},
	// The matrix keeps a small button/link, so `.link-sm` takes its values.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.link-sm': {
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '1rem',
		lineHeight: 1.56,
		letterSpacing: '-0.01em',
		fontWeight: 500,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:visited': {
			opacity: 0.8,
			'mix-blend-mode': 'multiply',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover': {
			opacity: 0.6,
			'mix-blend-mode': 'multiply',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:visited:hover': {
			opacity: 0.5,
			'mix-blend-mode': 'multiply',
		},
		'&:focus-visible': {
			border: 'none',
			borderRadius: '8px',
			outlineStyle: 'solid',
			outlineOffset: '2px',
			outlineWidth: '2px',
			outlineColor: 'inherit',
		},
	},

	// eyebrow
	// The refresh matrix defines a single EYEBROW token, so the three base-sheet sizes
	// converge here. `.eyebrow-lg`/`-md`/`-sm` remain distinct outside the refresh.
	//
	// These are kept as three separate rules rather than one grouped selector on purpose:
	// Compiled keys atomic rules off the selector string, so only a selector identical to
	// the base sheet's dedupes the base declaration away.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-lg': {
		fontWeight: 500,
		fontSize: '0.75rem',
		lineHeight: 1.2,
		letterSpacing: '0.08em',

		[media.above.md]: {
			fontSize: '1rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-md': {
		fontWeight: 500,
		fontSize: '0.75rem',
		lineHeight: 1.2,
		letterSpacing: '0.08em',

		[media.above.md]: {
			fontSize: '1rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-sm': {
		fontWeight: 500,
		fontSize: '0.75rem',
		lineHeight: 1.2,
		letterSpacing: '0.08em',

		[media.above.md]: {
			fontSize: '1rem',
		},
	},
	// Monobrow (code) — new in the refresh matrix, no equivalent in the base sheet.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.monobrow': {
		fontFamily: token('font.family.code'),
		fontWeight: 500,
		fontSize: '0.75rem',
		lineHeight: 1.2,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--wac-color-body)',

		[media.above.md]: {
			fontSize: '1rem',
		},
	},

	// callouts (quotes)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.callout': {
		fontWeight: 400,
		fontSize: '1.125rem',
		lineHeight: 1.36,
		letterSpacing: '-0.01em',

		[media.only.sm]: {
			fontSize: '1.25rem',
			lineHeight: 1.36,
		},
		[media.above.md]: {
			fontWeight: 400,
			fontSize: '1.375rem',
			lineHeight: 1.36,
		},
		[media.above.lg]: {
			fontSize: '1.5rem',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.callout-lg': {
		fontWeight: 400,
		fontSize: '1.25rem',
		lineHeight: 1.36,
		letterSpacing: '-0.01em',

		[media.only.sm]: {
			fontSize: '1.5rem',
			lineHeight: 1.36,
		},
		[media.above.md]: {
			fontWeight: 400,
			fontSize: '1.75rem',
			lineHeight: 1.36,
		},
		[media.above.lg]: {
			fontSize: '2rem',
		},
	},
	// The matrix drops the third callout size — only one was really being used — so
	// `.callout-sm` collapses onto `.callout`.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.callout-sm': {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 400,
		fontSize: '1.125rem',
		lineHeight: 1.36,
		letterSpacing: '-0.01em',
		color: 'var(--wac-color-body)',

		[media.only.sm]: {
			fontSize: '1.25rem',
		},
		[media.only.md]: {
			fontSize: '1.375rem',
		},
		[media.above.lg]: {
			fontSize: '1.5rem',
		},
	},
});

const typographyStylesAwf1 = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h1, h2, h3, h4, .h1, .h2, .h3, .h4, .h1-lg, .h1-xl, .h1-lg-contrast': {
		fontFamily: token('font.family.heading'),
		fontWeight: 400,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'p, .body-md, .body-lg': {
		fontFamily: token('font.family.body'),
		fontWeight: 400,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-md, .eyebrow-sm, .eyebrow-lg': {
		fontFamily: token('font.family.code'),
	},
});

interface TypographyStylesInterface {
	children: ReactNode;
}

/**
 * @description wrapper component used to add global font styles
 * If already inside another TypographyStyles component, it will skip re-rendering the styles
 */
export const TypographyStyles = ({ children }: TypographyStylesInterface) => {
	const isInsideTypographyStyles = useContext(TypographyStylesContext);
	const isWacRefresh = useWacRefresh();
	const { isAwf1Theme } = useTheme();

	// If we're already inside TypographyStyles, just render children without the styles
	if (isInsideTypographyStyles) {
		return <div>{children}</div>;
	}

	return (
		<TypographyStylesContext.Provider value={true}>
			<div
				css={[
					typographyStyles,
					isWacRefresh && typographyStylesRefresh,
					isAwf1Theme && typographyStylesAwf1,
				]}
			>
				{children}
			</div>
		</TypographyStylesContext.Provider>
	);
};
